'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { User } from '@/types/user/type';
import { VpButton, VpCheckbox, VpLink } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { fetchItem, getLogoutUri } from '@/app/actions/server';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { CustomSwipeableDrawer } from '~/components/commons/CustomSwipeableDrawer/CustomSwipeableDrawer';
import { GenericDialog } from '~/components/commons/genericDialog/GenericDialog';
import { isUserAdmin } from '~/components/Profile/utils';
import appConst from '~/const/appConst';
import { useBreakpoint } from '~/hooks/match-media/useBreakpoint';

export const DeleteAccountDialog = ({
  isOpen,
  setIsOpen,
  setIsDeletionConfirmed,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsDeletionConfirmed: Dispatch<SetStateAction<boolean>>;
}) => {
  const tDeleteAccount = useTranslations('SETTINGS.PERSONAL_DATA.DELETE_ACCOUNT');
  const tProfileGlobal = useTranslations('PROFILE.GLOBAL');
  const tGlobal = useTranslations('GLOBAL');

  const { user } = useAuthenticatedContext();
  const { setError } = useApiErrorContext();

  const { isSBreakpoint } = useBreakpoint();

  const [isPersonalDataAcknowledged, setIsPersonalDataAcknowledged]
    = useState(false);

  const handleCancel = () => {
    setIsOpen(false);
    setIsPersonalDataAcknowledged(false);
  };

  const handleDeleteUser = async () => {
    const response = await fetchItem<User>({
      pathname: `users/${user.id}/anonymize`,
      method: 'PUT',
      body: {},
      revalidate: 0,
    });

    if (response['hydra:description']) {
      handleCancel();
      return setError(response);
    }

    if (!isUserAdmin(user)) {
      await getLogoutUri({});
    } else {
      setIsOpen(false);
      setIsDeletionConfirmed(true);
    }
  };

  const content = (
    <div className="delete-account-dialog">
      <div>
        <p className="vp-body-m">
          {tDeleteAccount('MODAL.CONTENT')}
        </p>
        <p className="vp-body-m">
          {tDeleteAccount.rich('MODAL.INFORMATION')}
          {' '}
          <VpLink href={appConst.staticRoutes.privacy}>{tDeleteAccount('MODAL.LINK')}</VpLink>
        </p>
      </div>
      <div>
        <VpCheckbox
          data-testid="checkbox"
          className="delete-dialog-checkbox"
          checked={isPersonalDataAcknowledged}
          id="delete-content"
          name="delete_profile"
          onChange={() => setIsPersonalDataAcknowledged(val => !val)}
        >
          {tDeleteAccount('MODAL.CHECKBOX')}
        </VpCheckbox>
      </div>
    </div>
  );

  return isSBreakpoint
    ? (
        <CustomSwipeableDrawer
          dataTestId="delete-account-drawer"
          open={isOpen}
          title={tDeleteAccount('TITLE')}
          onClose={handleCancel}
          onOpen={() => setIsOpen(true)}
        >
          {content}
          <div className="delete-account-dialog__actions">
            <VpButton
              shape="squared"
              variant="secondary"
              onClick={handleCancel}
            >
              {tProfileGlobal('CANCEL')}
            </VpButton>
            <VpButton
              shape="squared"
              onClick={handleDeleteUser}
              disabled={!isPersonalDataAcknowledged}
            >
              {tGlobal('CONFIRM')}
            </VpButton>
          </div>
        </CustomSwipeableDrawer>
      )
    : (
        <GenericDialog
          isOpen={isOpen}
          primaryButtonTitle={tGlobal('CONFIRM')}
          primaryButtonClick={handleDeleteUser}
          isPrimaryButtonDisabled={!isPersonalDataAcknowledged}
          secondaryButtonTitle={tProfileGlobal('CANCEL')}
          secondaryButtonClick={handleCancel}
          onClose={handleCancel}
          content={content}
          title={tDeleteAccount('TITLE')}
        />
      );
};
